import { Component, ElementRef, ViewChild, Input, SimpleChanges, Renderer2, Output, EventEmitter, OnInit, AfterViewInit, OnChanges, OnDestroy }
	from '@angular/core';
import { Subscription, fromEvent, ReplaySubject } from 'rxjs';
import { BbDragEvent } from 'app/directives/bb-drag.directive';

type fromPosition = 'top' | 'right' | 'bottom' | 'left';
declare let ResizeObserver: any;
@Component({
	selector: 'bb-resizable-scrollbar',
	templateUrl: './bb-resizable-scrollbar.component.html',
	styleUrls: ['./bb-resizable-scrollbar.component.scss']
})
export class BbResizableScrollbarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
	@Input() orientation: 'horizontal' | 'vertical' = 'horizontal';
	@Input() useResize = true;
	@Input() hideBackground = false;
	@Input() barColor: string;
	@Input() scrollElement: HTMLElement;
	@Input() subject: ReplaySubject<{
		zoomFactor: number;
		offset: number;
		from: fromPosition;
	}>;
	@Input() autoHide = false;
	@Output() dragstart = new EventEmitter();
	@Output() dragging = new EventEmitter();
	@Output() dragend = new EventEmitter();
	@ViewChild('container', { static: true }) containerRef: ElementRef;
	@ViewChild('bar', { static: true }) barRef: ElementRef;
	@ViewChild('topHandle', { static: true }) topHandleRef: ElementRef;
	@ViewChild('rightHandle', { static: true }) rightHandleRef: ElementRef;
	@ViewChild('bottomHandle', { static: true }) bottomHandleRef: ElementRef;
	@ViewChild('leftHandle', { static: true }) leftHandleRef: ElementRef;

	updating = false;
	handles: any = {};
	container: HTMLElement;
	bar: HTMLElement;
	// scrollElement: HTMLElement;
	offsetProp: 'left' | 'top';
	sizeProp: 'width' | 'height';
	lastFrom: fromPosition;

	barProps = {
		offset: 0, // left or top
		size: 1, // width or height
		offsetPct: 0,
		sizePct: 1
	};
	startProps: any = {
		elementSize: 1,
		barProps: this.barProps
	};
	mouseIsOver = false;
	fadeOutTimeout: any = null;
	minimumBarSize = 40;

	private resizeObserver: any;
	private subscriptions = new Subscription();

	constructor(
		private el: ElementRef,
		private renderer: Renderer2
	) {

	}

	ngOnInit() {
		this.container = this.containerRef.nativeElement;
		this.bar = this.barRef.nativeElement;
		this.handles = {
			top: this.topHandleRef && this.topHandleRef.nativeElement,
			right: this.rightHandleRef && this.rightHandleRef.nativeElement,
			bottom: this.bottomHandleRef && this.bottomHandleRef.nativeElement,
			left: this.leftHandleRef && this.leftHandleRef.nativeElement
		};
		this.update();
		if (this.subject) {
			this.subscriptions.add(this.subject.subscribe((zoomInfo) => {
				this.barProps.sizePct = zoomInfo.zoomFactor * 100;
				this.barProps.offsetPct = zoomInfo.offset * 100;
				this.updateBar();
			}));
		}
		this.initResizeObserver();
	}

	ngAfterViewInit() {
		setTimeout(() => {
			if (this.scrollElement) {
				this.initiateElement();
			} else {
				this.initBar();
				this.updateView();
			}
			this.fade('out');
			this.dispatchChange();
		}, 10);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.orientation && changes.orientation.previousValue !== changes.orientation.currentValue) {
			this.update();
		}
	}

	initBar() {
		this.barProps.size = (this.orientation === 'horizontal' ? this.container.offsetWidth : this.container.offsetHeight);
		this.calcBarPropPcts();
	}

	calcBarPropPcts() {
		const s = (this.orientation === 'horizontal' ? this.container.offsetWidth : this.container.offsetHeight);
		this.barProps.sizePct = (this.barProps.size / s) * 100;
		this.barProps.offsetPct = (this.barProps.offset / s) * 100;
	}

	initiateElement() {
		this.subscriptions.add(fromEvent(this.scrollElement, 'scroll').subscribe((ev: Event) => {
			this.onScroll(ev);
		}));
		this.subscriptions.add(fromEvent(this.scrollElement, 'wheel').subscribe((ev: WheelEvent) => {
			this.onWheel(ev);
		}));
		this.updateBar();
	}

	onWheel(ev: WheelEvent) {
		if (this.orientation === 'horizontal') {
			this.scrollElement.scrollLeft += ev.deltaX;
		} else {
			this.scrollElement.scrollTop += ev.deltaY;
		}
	}

	onMouseOver(ev: MouseEvent) {
		this.mouseIsOver = true;
		this.fade('in');
	}

	onMouseOut(ev: MouseEvent) {
		this.mouseIsOver = false;
		this.fade('out');
	}

	fade(inOut: 'in' | 'out') {
		if (this.autoHide) {
			if (this.fadeOutTimeout) {
				clearTimeout(this.fadeOutTimeout);
			}

			if (inOut === 'in') {
				this.el.nativeElement.style.opacity = 1;
			} else {
				this.fadeOutTimeout = setTimeout(() => {
					this.el.nativeElement.style.opacity = 0;
				}, 1500);
			}
		}
	}

	updateBar() {
		if (this.scrollElement) {
			this.updateSizeFromElement();
			this.updatePositionFromElement();
		} else {
			this.updateBarFromPct();
		}

		this.updateView();
	}

	updateBarFromPct() {
		const containerSize = (this.orientation === 'horizontal' ? this.container.offsetWidth : this.container.offsetHeight);
		this.barProps.size = (this.barProps.sizePct / 100) * containerSize;
		this.barProps.offset = (this.barProps.offsetPct / 100) * containerSize;
	}

	onScroll(ev: Event) {
		if (!this.updating) {
			this.updatePositionFromElement();
			this.updateView();
			this.fade('in');
			this.fade('out');
		}
	}

	updatePositionFromElement() {
		if (this.scrollElement && this.container) {
			const s = this.orientation === 'horizontal' ? this.container.scrollWidth : this.container.scrollHeight;
			this.barProps.offset = (this.orientation === 'horizontal' ?
				this.scrollElement.scrollLeft / this.scrollElement.scrollWidth :
				this.scrollElement.scrollTop / this.scrollElement.scrollHeight)
				* s;
			this.calcBarPropPcts();
		}
	}

	updateSizeFromElement() {
		if (this.scrollElement && this.container) {
			const s = this.orientation === 'horizontal' ? this.container.scrollWidth : this.container.scrollHeight;
			this.barProps.size = (this.orientation === 'horizontal' ?
				this.scrollElement.offsetWidth / this.scrollElement.scrollWidth :
				this.scrollElement.offsetHeight / this.scrollElement.scrollHeight)
				* s;
			this.calcBarPropPcts();
		}
	}
	onClick(ev: MouseEvent) {
		// console.warn('click', ev);
	}

	updateElement(updateSize = false) {
		setTimeout(() => {
			// setTimeout to not block DOM rendering and allow smooth dragging
			if (this.scrollElement) {
				if (this.orientation === 'horizontal' && updateSize) {
					(this.scrollElement.firstElementChild as any).style[this.sizeProp] =
						this.scrollElement.offsetWidth * (this.container.offsetWidth / this.barProps.size) + 'px';
				}
				if (this.orientation === 'horizontal') {
					this.scrollElement.scrollLeft = ((this.barProps.offset / this.container.offsetWidth) * this.scrollElement.scrollWidth);
				} else {
					this.scrollElement.scrollTop = ((this.barProps.offset / this.container.offsetHeight) * this.scrollElement.scrollHeight);
				}
			}
			this.dispatchChange();
		});
	}

	dispatchChange() {
		const zoomInfo = {
			zoomFactor: this.barProps.size / (this.orientation === 'horizontal' ? this.container.offsetWidth : this.container.offsetHeight),
			offset: this.barProps.offset / (this.orientation === 'horizontal' ? this.container.offsetWidth : this.container.offsetHeight),
			from: this.lastFrom
		};
		if (this.subject) {
			this.subject.next(zoomInfo);
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}

	public update() {
		this.offsetProp = this.orientation === 'horizontal' ? 'left' : 'top';
		this.sizeProp = this.orientation === 'horizontal' ? 'width' : 'height';
		if (this.container && this.scrollElement) {
			this.updateBar();
		}
	}

	initResizeObserver() {
		this.resizeObserver = new ResizeObserver(entries => {
			requestAnimationFrame(() => {
				this.onContainerResize();
			});
		});

		this.resizeObserver.observe(this.containerRef.nativeElement);
	}

	onContainerResize() {
		this.updateBar();
	}

	onDragStart(ev: any) {
		this.updating = true;
		this.dragstart.emit();
	}

	onDragging(ev: any) {
		// console.warn('ev', ev);
		this.updateOffsetFromBar();
		this.updateElement();
		this.dragging.emit();
	}

	onDragEnd(ev: any) {
		this.updateOffsetFromBar();
		this.updateHandlePositions();
		this.updateElement();
		this.updating = false;
		this.dragend.emit();
	}

	onResizeHandleClick(from: fromPosition) {
		if (this.lastFrom !== from) {
			this.lastFrom = from;
			this.dispatchChange();
		}
	}

	updateOffsetFromBar() {
		this.barProps.offset = (this.bar.getBoundingClientRect()[this.offsetProp] - this.container.getBoundingClientRect()[this.offsetProp]);
		this.calcBarPropPcts();
	}

	updateSize(from: fromPosition, el: HTMLElement, fromStart = false) {
		this.lastFrom = from;
		const previousBarProps = { ...this.barProps };
		// el is resizable element
		const elRect = el.getBoundingClientRect();
		const offset = elRect[this.offsetProp] - this.container.getBoundingClientRect()[this.offsetProp] +
			(from === 'right' || from === 'bottom' ? elRect[this.sizeProp] : 0);
		if (fromStart) {
			this.startProps = {
				barProps: { ...this.barProps }
			};
			if (this.scrollElement) {
				this.startProps.elementSize = this.orientation === 'horizontal' ? this.scrollElement.scrollWidth : this.scrollElement.scrollHeight;

			}
		}
		if (from === 'right' || from === 'bottom') {
			this.barProps.size = offset - this.barProps.offset;
		}
		if (from === 'left' || from === 'top') {
			this.barProps.offset = offset;
			this.barProps.size = this.startProps.barProps.size + (this.startProps.barProps.offset - this.barProps.offset);
		}

		if (this.barProps.size < this.minimumBarSize) {
			this.barProps.size = previousBarProps.size;
			this.barProps.offset = previousBarProps.offset;
		}
		this.calcBarPropPcts();

		if (this.barProps.size !== previousBarProps.size) {
			this.updateView(true);
		}
		this.checkVisibility();
	}

	updateView(updatingSize = false) {
		this.renderer.setStyle(this.bar, this.offsetProp, this.barProps.offset + 'px');
		this.renderer.setStyle(this.bar, this.sizeProp, this.barProps.size + 'px');
		// this.renderer.setStyle(this.bar, this.offsetProp, this.barProps.offsetPct + '%');
		// this.renderer.setStyle(this.bar, this.sizeProp, this.barProps.sizePct + '%');
		if (!updatingSize) {
			this.updateHandlePositions();
		}

		this.checkVisibility();
	}

	checkVisibility() {
		if (this.autoHide && this.container) {
			const s = (this.orientation === 'horizontal' ? this.container.offsetWidth : this.container.offsetHeight);
			if (s === this.barProps.size) {
				this.el.nativeElement.style.opacity = 0;
				this.el.nativeElement.style.pointerEvents = 'none';
				return;
			} else {
				this.el.nativeElement.style.pointerEvents = 'all';
			}
		}
	}

	updateHandlePositions() {
		if (this.orientation === 'horizontal') {
			this.renderer.setStyle(this.handles.left, 'left', `${this.barProps.offset}px`);
			this.renderer.setStyle(this.handles.right, 'left', `${(this.barProps.offset + this.barProps.size) - this.handles.right.offsetWidth}px`);
		} else {
			this.renderer.setStyle(this.handles.left, 'top', `${this.barProps.offset}px`);
			this.renderer.setStyle(this.handles.right, 'top', `${(this.barProps.offset + this.barProps.size) - this.handles.right.offsetHeight}px`);
		}
	}

	onResizeStart(ev: BbDragEvent, from: fromPosition) {
		this.dragstart.emit();
		this.lastFrom = from;
		this.updating = true;
		this.updateSize(from, ev.source.element, true);
	}

	onResizing(ev: any, from: fromPosition) {
		this.dragging.emit();
		this.lastFrom = from;
		this.updateSize(from, ev.source.element, true);
		this.updateElement(true);
	}

	onResizeEnd(ev: any, from: fromPosition) {
		this.dragend.emit();
		this.lastFrom = from;
		this.updateSize(from, ev.source.element, true);
		this.updateElement(true);
		this.updating = false;
		setTimeout(() => {
			this.updateHandlePositions();
		});
	}
}
